import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Video, Calendar, Phone, MapPin, Clock, Star, Award, Users, Heart, Shield, CheckCircle, MessageCircle, Mail, Baby, Activity, Stethoscope, TrendingUp } from "lucide-react";

const DelhiPage: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalOpen2, setIsModalOpen2] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);
    const openModal2 = () => setIsModalOpen2(true);
    const closeModal2 = () => setIsModalOpen2(false);

    const slides = [
        { position: "center", class: "object-center" },
        { position: "left", class: "object-left" },
        { position: "right", class: "object-right" },
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prevSlide: any) => (prevSlide + 1) % slides.length);
        }, 5000);

        return () => clearInterval(timer);
    }, []);

    const delhiLocations = [
        { name: "All India Institute of Medical Sciences (AIIMS)", days: "Monday & Thursday", time: "9am-12pm", area: "AIIMS" },
        { name: "Safdarjung Hospital", days: "Tuesday & Friday", time: "2-5pm", area: "Safdarjung" },
        { name: "Max Super Speciality Hospital", days: "Wednesday & Saturday", time: "10am-1pm", area: "Saket" },
        { name: "Apollo Hospital", days: "Monday & Wednesday", time: "4-7pm", area: "Mathura Road" },
        { name: "Fortis Healthcare", days: "Thursday & Friday", time: "11am-2pm", area: "Shalimar Bagh" },
        { name: "BLK Super Speciality Hospital", days: "Tuesday & Saturday", time: "3-6pm", area: "Pusa Road" }
    ];

    const pediatricServices = [
        "Pediatric Diabetes Management",
        "Growth Hormone Disorders",
        "Short Stature Treatment",
        "Thyroid Disorders in Children",
        "Puberty Disorders",
        "Childhood Obesity Management",
        "PCOS in Adolescent Girls",
        "Adrenal Disorders",
        "Bone & Calcium Disorders",
        "Insulin Therapy for Kids",
        "Type 1 Diabetes in Children",
        "Continuous Glucose Monitoring"
    ];

    const delhiAreas = [
        "Connaught Place", "Karol Bagh", "Rajouri Garden", "Janakpuri", "Dwarka",
        "Rohini", "Pitampura", "Shalimar Bagh", "Model Town", "Civil Lines",
        "Delhi Cantt", "Patel Nagar", "Punjabi Bagh", "Tilak Nagar", "Laxmi Nagar",
        "Preet Vihar", "Mayur Vihar", "IP Extension", "Patparganj", "Shahdara"
    ];

    return (
        <main className="bg-white">
            {/* Hero Section */}
            <section id="landing-hero" className="relative mt-28 md:mt-48">
                <div className="relative w-full h-screen max-h-screen overflow-hidden">
                    {slides.map((slide, index) => (
                        <motion.img
                            key={index}
                            src="/images/banner.jpg"
                            alt={`Best pediatric endocrinologist in Delhi ${slide.position}`}
                            className={`absolute w-full h-full object-cover ${slide.class}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: index === currentSlide ? 1 : 0 }}
                            transition={{ duration: 0.5 }}
                        />
                    ))}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/40 flex flex-col justify-center items-center text-center px-4 sm:px-8 md:px-12 lg:px-16">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="max-w-4xl mx-auto mt-24">

                            <h2 className="font-work-sans text-2xl sm:text-3xl md:text-4xl text-yellow-300 mb-4 font-bold">
                                Best Pediatric Endocrinologist in Delhi | Child Hormone Specialist
                            </h2>
                            <h1 className="font-fraunces-slab text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4 text-white">
                                Dr. Swati Kanodia
                            </h1>
                            <p className="text-white text-xl md:text-2xl mb-2">
                                Leading Child Hormone & Growth Specialist in Delhi, AIIMS, Safdarjung
                            </p>
                            <p className="text-yellow-200 text-lg mb-6">
                                ✓ 10+ Years Experience ✓ 5000+ Happy Kids ✓ Growth & Diabetes Expert ✓ Multiple Delhi Locations
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={openModal}
                                    className="bg-purple-600 text-white font-pt-serif-regular px-8 py-4 rounded-lg flex items-center justify-center text-lg transition-all duration-300 hover:bg-purple-700 hover:scale-105 shadow-lg"
                                >
                                    Book Appointment
                                    <Calendar className="ml-3 w-5 h-5" />
                                </button>
                                <button
                                    onClick={openModal2}
                                    className="bg-green-600 text-white font-pt-serif-regular px-8 py-4 rounded-lg flex items-center justify-center text-lg transition-all duration-300 hover:bg-green-700 hover:scale-105 shadow-lg"
                                >
                                    Online Consultation
                                    <Video className="ml-3 w-5 h-5" />
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Quick Stats */}
            <section className="bg-purple-900 text-white py-12">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        <div>
                            <Baby className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
                            <h3 className="text-2xl font-bold">5000+</h3>
                            <p>Children Treated</p>
                        </div>
                        <div>
                            <TrendingUp className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
                            <h3 className="text-2xl font-bold">95%</h3>
                            <p>Success Rate</p>
                        </div>
                        <div>
                            <MapPin className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
                            <h3 className="text-2xl font-bold">6</h3>
                            <p>Delhi Clinics</p>
                        </div>
                        <div>
                            <Star className="w-12 h-12 mx-auto mb-4 text-yellow-300" />
                            <h3 className="text-2xl font-bold">4.9/5</h3>
                            <p>Parent Rating</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section>
                <motion.section
                    id="pediatric-endocrinologist-delhi"
                    className="bg-[#faf9f5] py-20 px-4 sm:px-6 lg:px-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-3xl md:text-5xl text-purple-900 font-fraunces-slab text-center mb-12">
                            Looking for Best Pediatric Endocrinologist in Delhi?
                        </h2>

                        <div className="grid md:grid-cols-2 gap-8 mb-12">
                            <div>
                                <p className="text-lg md:text-xl text-gray-800 mb-6 font-pt-serif-regular leading-relaxed">
                                    Finding a qualified and experienced <strong>pediatric endocrinologist in Delhi</strong> for your child's
                                    hormone-related health issues can be challenging. Many parents struggle to find the right specialist
                                    who understands children's unique endocrine needs in the busy Delhi metropolitan area.
                                </p>

                                <p className="text-lg md:text-xl text-gray-800 mb-6 font-pt-serif-regular leading-relaxed">
                                    Dr. Swati Kanodia is Delhi's most trusted <strong>child hormone specialist and pediatric endocrinologist</strong>
                                    with over 10 years of experience treating growth disorders, diabetes, thyroid issues, and puberty-related
                                    problems in children across AIIMS, Safdarjung, Karol Bagh, Connaught Place, and surrounding Delhi areas.
                                </p>

                                <div className="bg-blue-100 p-6 rounded-lg border-l-4 border-blue-500">
                                    <h3 className="text-xl font-bold text-purple-900 mb-3">Why Parents Choose Dr. Swati Kanodia?</h3>
                                    <ul className="space-y-2">
                                        <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-600 mr-2" /> Child-friendly approach & environment</li>
                                        <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-600 mr-2" /> 10+ years pediatric endocrine expertise</li>
                                        <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-600 mr-2" /> Multiple Delhi locations including AIIMS</li>
                                        <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-600 mr-2" /> Affordable consultation & treatment</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-bold text-purple-900 mb-6">Specialized Treatment Areas</h3>
                                <div className="space-y-4">
                                    <div className="flex items-start">
                                        <Baby className="w-6 h-6 text-pink-500 mr-3 mt-1 flex-shrink-0" />
                                        <div>
                                            <h4 className="font-semibold text-gray-800">Growth Disorders</h4>
                                            <p className="text-gray-600">Short stature, growth hormone deficiency treatment</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <Activity className="w-6 h-6 text-red-500 mr-3 mt-1 flex-shrink-0" />
                                        <div>
                                            <h4 className="font-semibold text-gray-800">Pediatric Diabetes</h4>
                                            <p className="text-gray-600">Type 1 & Type 2 diabetes management in children</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <Stethoscope className="w-6 h-6 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                                        <div>
                                            <h4 className="font-semibold text-gray-800">Thyroid Issues</h4>
                                            <p className="text-gray-600">Hypothyroidism, hyperthyroidism in kids</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <TrendingUp className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                                        <div>
                                            <h4 className="font-semibold text-gray-800">Puberty Disorders</h4>
                                            <p className="text-gray-600">Early/delayed puberty, PCOS in teens</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="text-center mb-12">
                            <span className="bg-pink-300 text-gray-800 px-8 py-3 rounded-full text-lg font-semibold">
                                #1 Pediatric Endocrinologist in Delhi
                            </span>
                        </div>
                    </div>
                </motion.section>
            </section>

            {/* Services Section */}
            <section className="py-20 bg-white">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-3xl md:text-5xl text-purple-900 font-fraunces-slab text-center mb-12">
                        Pediatric Endocrinology Services in Delhi
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {pediatricServices.map((service, index) => (
                            <motion.div
                                key={index}
                                className="bg-gradient-to-br from-pink-50 to-purple-50 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Baby className="w-8 h-8 text-pink-600 mb-3" />
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">{service}</h3>
                                <p className="text-gray-600 text-sm">Expert pediatric care for optimal child health and development</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Delhi Locations */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-3xl md:text-5xl text-purple-900 font-fraunces-slab text-center mb-12">
                        Clinic Locations Across Delhi
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                        {delhiLocations.map((location, index) => (
                            <motion.div
                                key={index}
                                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <MapPin className="w-8 h-8 text-purple-600 mb-3" />
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">{location.name}</h3>
                                <p className="text-purple-600 font-semibold mb-2">{location.area}</p>
                                <p className="text-gray-600 mb-1">
                                    <Clock className="w-4 h-4 inline mr-1" />
                                    {location.days}
                                </p>
                                <p className="text-purple-600 font-semibold">{location.time}</p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-8 rounded-lg">
                        <h3 className="text-2xl font-bold text-purple-900 mb-4 text-center">Areas We Serve in Delhi</h3>
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-3 text-center">
                            {delhiAreas.map((area, index) => (
                                <span key={index} className="bg-white px-3 py-2 rounded-lg text-sm font-medium text-gray-700 shadow-sm">
                                    {area}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Section */}
            <section className="py-20 bg-purple-900 text-white">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-3xl md:text-5xl font-fraunces-slab text-center mb-12">
                        Why Dr. Swati Kanodia is Delhi's #1 Choice for Children?
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <p className="text-lg mb-6 leading-relaxed">
                                Among all pediatric endocrinologists in Delhi, Dr. Swati Kanodia has earned the trust of
                                thousands of parents through her exceptional care and proven results in treating children's
                                hormone-related conditions across the capital city:
                            </p>

                            <div className="space-y-4">
                                <div className="bg-white/10 backdrop-blur p-4 rounded-lg">
                                    <h4 className="font-semibold text-yellow-300 mb-2">1. Child-Centric Approach</h4>
                                    <p className="text-gray-200">Creating comfortable, fear-free environment for children</p>
                                </div>
                                <div className="bg-white/10 backdrop-blur p-4 rounded-lg">
                                    <h4 className="font-semibold text-yellow-300 mb-2">2. Proven Track Record</h4>
                                    <p className="text-gray-200">5000+ children successfully treated with 95% success rate</p>
                                </div>
                                <div className="bg-white/10 backdrop-blur p-4 rounded-lg">
                                    <h4 className="font-semibold text-yellow-300 mb-2">3. Prestigious Hospital Network</h4>
                                    <p className="text-gray-200">Associated with AIIMS, Safdarjung & top Delhi hospitals</p>
                                </div>
                                <div className="bg-white/10 backdrop-blur p-4 rounded-lg">
                                    <h4 className="font-semibold text-yellow-300 mb-2">4. Comprehensive Care</h4>
                                    <p className="text-gray-200">Complete pediatric endocrine care under one roof</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white/10 backdrop-blur p-8 rounded-lg">
                            <h3 className="text-2xl font-bold mb-6 text-yellow-300">Delhi's Premier Child Specialist</h3>
                            <div className="space-y-6">
                                <div className="pb-4">
                                    <h4 className="font-semibold text-white mb-2">AIIMS Association</h4>
                                    <p className="text-gray-200">Associated with India's premier medical institution for the highest standards of care</p>
                                </div>
                                <div className="pb-4">
                                    <h4 className="font-semibold text-white mb-2">Advanced Technology</h4>
                                    <p className="text-gray-200">Latest diagnostic equipment and treatment methods for accurate results</p>
                                </div>
                                <div className="pb-4">
                                    <h4 className="font-semibold text-white mb-2">Accessible Locations</h4>
                                    <p className="text-gray-200">Multiple clinics across Delhi for convenient access from any area</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="text-center mt-12">
                        <span className="bg-pink-300 text-gray-800 px-8 py-3 rounded-full text-lg font-semibold">
                            Most Trusted Child Hormone Specialist in Delhi
                        </span>
                    </div>
                </div>
            </section>

            {/* Treatment Approach Section */}
            <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-3xl md:text-5xl text-purple-900 font-fraunces-slab text-center mb-12">
                        Our Child-Friendly Treatment Approach
                    </h2>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                            <Heart className="w-16 h-16 text-pink-500 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-purple-900 mb-4">Comfort First</h3>
                            <p className="text-gray-600">
                                Child-friendly clinics with toys, colorful decor, and gentle staff to make
                                medical visits comfortable and stress-free for children.
                            </p>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                            <Users className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-purple-900 mb-4">Family Support</h3>
                            <p className="text-gray-600">
                                Comprehensive family counseling and education to help parents understand
                                and manage their child's condition at home effectively.
                            </p>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                            <Shield className="w-16 h-16 text-green-500 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-purple-900 mb-4">Latest Technology</h3>
                            <p className="text-gray-600">
                                State-of-the-art diagnostic equipment and latest treatment methods
                                ensuring accurate diagnosis and effective treatment outcomes.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="py-20 bg-white">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-3xl md:text-5xl text-purple-900 font-fraunces-slab text-center mb-12">
                        Book Appointment - Pediatric Endocrinologist Delhi
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-lg shadow-lg">
                            <h3 className="text-2xl font-bold text-purple-900 mb-6">Schedule Your Child's Consultation</h3>

                            <div className="space-y-4">
                                <div className="flex items-center">
                                    <Phone className="w-6 h-6 text-green-600 mr-4" />
                                    <div>
                                        <p className="font-semibold">Call/WhatsApp for Appointment</p>
                                        <p className="text-gray-600">Quick response during consultation hours</p>
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <Video className="w-6 h-6 text-blue-600 mr-4" />
                                    <div>
                                        <p className="font-semibold">Video Consultation Available</p>
                                        <p className="text-gray-600">Online consultations for follow-ups</p>
                                    </div>
                                </div>

                                <div className="flex items-center">
                                    <Clock className="w-6 h-6 text-purple-600 mr-4" />
                                    <div>
                                        <p className="font-semibold">Flexible Timing</p>
                                        <p className="text-gray-600">Multiple time slots across Delhi locations</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 flex flex-col sm:flex-row gap-4">
                                <button
                                    onClick={openModal}
                                    className="bg-purple-600 text-white px-6 py-3 rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-purple-700"
                                >
                                    Book In-Person Visit
                                    <Calendar className="ml-2 w-5 h-5" />
                                </button>
                                <button
                                    onClick={openModal2}
                                    className="bg-green-600 text-white px-6 py-3 rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-green-700"
                                >
                                    Online Consultation
                                    <Video className="ml-2 w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
                            <h3 className="text-2xl font-bold text-purple-900 mb-6">What Makes Us Different?</h3>

                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                                    <div>
                                        <h4 className="font-semibold">AIIMS Standards</h4>
                                        <p className="text-gray-600 text-sm">Highest quality care following AIIMS protocols</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                                    <div>
                                        <h4 className="font-semibold">Quick Diagnosis</h4>
                                        <p className="text-gray-600 text-sm">Advanced diagnostic facilities for faster results</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                                    <div>
                                        <h4 className="font-semibold">Metro Connectivity</h4>
                                        <p className="text-gray-600 text-sm">Easy access via Delhi Metro from all locations</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                                    <div>
                                        <h4 className="font-semibold">24/7 Emergency Support</h4>
                                        <p className="text-gray-600 text-sm">Emergency helpline for urgent concerns</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-3xl md:text-4xl font-fraunces-slab mb-6">
                        Don't Wait - Your Child's Health is Our Priority
                    </h2>
                    <p className="text-xl mb-8 text-purple-100">
                        Book consultation with Delhi's most trusted pediatric endocrinologist today
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={openModal}
                            className="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:bg-gray-100 hover:scale-105 shadow-lg"
                        >
                            Book Appointment Now
                        </button>
                        <button
                            onClick={openModal2}
                            className="bg-green-500 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:bg-green-600 hover:scale-105 shadow-lg"
                        >
                            Start Online Consultation
                        </button>
                    </div>
                </div>
            </section>

            {/* Appointment Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 relative">


                        <div className="text-center">
                            <Calendar className="w-16 h-16 text-purple-600 mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">Book Appointment</h3>
                            <p className="text-gray-600 mb-6">
                                Contact Dr. Swati Kanodia for consultation and appointment booking
                            </p>

                            <div className="space-y-4">
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-900 mb-2">Consultation Hours</h4>
                                    <p className="text-gray-700">Monday - Saturday: 11am - 7pm</p>
                                    <p className="text-sm text-gray-600">Sunday: Emergency only (additional charges apply)</p>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-900 mb-2">Contact Information</h4>
                                    <p className="text-gray-700">Call/Message for appointment booking</p>
                                    <p className="text-sm text-gray-600">Quick response during consultation hours</p>
                                </div>

                                <div className="bg-green-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-900 mb-2">Multiple Locations</h4>
                                    <p className="text-gray-700">Choose from 5+ convenient South Delhi clinics</p>
                                </div>
                            </div>

                            <div className="mt-6">
                                <a
                                    href="https://storynavigation.com/user/drswatikanodia"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-purple-600 text-white px-6 py-3 rounded-lg inline-block font-semibold hover:bg-purple-700 transition-colors"
                                >
                                    Book Now
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Online Consultation Modal */}
            {isModalOpen2 && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 relative">


                        <div className="text-center">
                            <Video className="w-16 h-16 text-green-600 mx-auto mb-4" />
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">Online Consultation</h3>
                            <p className="text-gray-600 mb-6">
                                Get expert pediatric endocrinology consultation from the comfort of your home
                            </p>

                            <div className="space-y-4">
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-900 mb-2">Video Consultation</h4>
                                    <p className="text-gray-700">Face-to-face consultation via video call</p>
                                    <p className="text-sm text-gray-600">Perfect for follow-ups and initial assessments</p>
                                </div>

                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-900 mb-2">Available Timings</h4>
                                    <p className="text-gray-700">Monday - Saturday: 11am - 7pm</p>
                                    <p className="text-sm text-gray-600">Flexible slots for working parents</p>
                                </div>

                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-900 mb-2">What You Need</h4>
                                    <p className="text-gray-700">Stable internet connection & previous reports</p>
                                </div>
                            </div>

                            <div className="mt-6">
                                <a
                                    href="https://storynavigation.com/user/drswatikanodia"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-green-600 text-white px-6 py-3 rounded-lg inline-block font-semibold hover:bg-green-700 transition-colors"
                                >
                                    Start Online Consultation
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default DelhiPage;